import { addDays } from "date-fns";
import _ from "lodash";
import { useCallback, useReducer } from "react";
import i18n from "../../services/localization-service/i18n";
import storageService from "../../services/storage/storage-service/StorageService";
import { ITrendingDisplayOptions } from "../../services/trending-data-service/Models";
import { CustomDurationDialogValues } from "../custom-duration-dialog/CustomDurationDialog";
import CustomDurationUtils, { DurationOptionEnum } from "../custom-duration-dialog/CustomDurationUtils";
import ObjectGraphUtils from "../object-graph/ObjectGraphUtils";

const TANK_BATTERY_TAG_VALUES_VIEW_OPTION_STORAGE_KEY = "TankBatteryTagValuesViewOptionsState";

type TankBatteryTagValuesViewOptionsState = {
    durationLabel: string;
    durationOptions: CustomDurationDialogValues;
    graphDisplayOptions: ITrendingDisplayOptions;
};

export type TankBatteryTagValueState = {
    options: TankBatteryTagValuesViewOptionsState;
    isOptionsDirty: boolean;
};

const initialOptionsState: TankBatteryTagValuesViewOptionsState = {
    durationLabel: i18n.t('customDuration.configured'),
    durationOptions: {
        option: DurationOptionEnum.Default,
        duration: {
            startDate: addDays(new Date(), -7),
            endDate: new Date()
        }
    },
    graphDisplayOptions: ObjectGraphUtils.getDefaultDisplayOptions().displayOptions!
};

const initialState: TankBatteryTagValueState = {
    options: initialOptionsState,
    isOptionsDirty: false
};

function getInitialState() {

    // if reading options state from local storage, dates will be strings, so they have to be 
    // fixed here
    const storedOptionState: TankBatteryTagValuesViewOptionsState | undefined = storageService.local.get<TankBatteryTagValuesViewOptionsState | undefined>(TANK_BATTERY_TAG_VALUES_VIEW_OPTION_STORAGE_KEY, undefined);

    if (storedOptionState) {
        if (storedOptionState.durationOptions.duration.startDate) {
            storedOptionState.durationOptions.duration.startDate = new Date(storedOptionState.durationOptions.duration.startDate.toString());
        }
        if (storedOptionState.durationOptions.duration.endDate) {
            storedOptionState.durationOptions.duration.endDate = new Date(storedOptionState.durationOptions.duration.endDate.toString());
        }
    }

    const newState = {
        ...initialState,
        options: storedOptionState ?? initialOptionsState
    };

    return newState;
}

enum TankBatteryTagValueActionType {
    ResetOptionsRequested = 'RESET_OPTIONS_REQUESTED',
    DurationChanged = 'DURATION_CHANGED',
    GraphDisplayOptionsChanged = "GRAPH_DISPLAY_OPTIONS_CHANGED",
}

type TankBatteryTagValueActions = | {
    type: TankBatteryTagValueActionType.ResetOptionsRequested;
} | {
    type: TankBatteryTagValueActionType.DurationChanged;
    payload: CustomDurationDialogValues;
} | {
    type: TankBatteryTagValueActionType.GraphDisplayOptionsChanged;
    payload: ITrendingDisplayOptions;
};

function tankBatteryTagValuesReducer(state: TankBatteryTagValueState, action: TankBatteryTagValueActions): TankBatteryTagValueState {

    let newState: TankBatteryTagValueState = {
        ...state,

        options: {
            ...state.options,
            graphDisplayOptions: { ...state.options.graphDisplayOptions },
            durationLabel: state.options.durationLabel,
            durationOptions: {
                option: state.options.durationOptions.option,
                duration: {
                    startDate: new Date(state.options.durationOptions.duration.startDate as Date),
                    endDate: new Date(state.options.durationOptions.duration.endDate as Date)
                }
            } as CustomDurationDialogValues
        }
    };

    switch (action.type) {

        case TankBatteryTagValueActionType.GraphDisplayOptionsChanged:
            newState.options = {
                ...newState.options,
                graphDisplayOptions: action.payload
            };
            newState.isOptionsDirty = isOptionsDirty(newState);
            return newState;

        case TankBatteryTagValueActionType.ResetOptionsRequested:
            storageService.local.remove(TANK_BATTERY_TAG_VALUES_VIEW_OPTION_STORAGE_KEY);

            newState.options = {
                ...getInitialState().options,
                graphDisplayOptions: { ...getInitialState().options.graphDisplayOptions },
                durationOptions: {
                    option: getInitialState().options.durationOptions.option,
                    duration: {
                        startDate: new Date(getInitialState().options.durationOptions.duration.startDate as Date),
                        endDate: new Date(getInitialState().options.durationOptions.duration.endDate as Date)
                    }
                }
            };

            newState.isOptionsDirty = isOptionsDirty(newState);
            return newState;

        case TankBatteryTagValueActionType.DurationChanged:

            newState.options = {
                ...state.options,
                durationLabel: action.payload.option === DurationOptionEnum.Default ?
                    i18n.t('customDuration.configured') :
                    CustomDurationUtils.getCustomDurationLabel(action.payload.option, true),
                durationOptions: {
                    option: action.payload.option,
                    duration: {
                        startDate: new Date(action.payload.duration.startDate as Date),
                        endDate: new Date(action.payload.duration.endDate as Date)
                    }
                }
            };

            newState.isOptionsDirty = isOptionsDirty(newState);
            storageService.local.set(TANK_BATTERY_TAG_VALUES_VIEW_OPTION_STORAGE_KEY, newState.options);
            return newState;

        default:
            return state;
    }
}

function isOptionsDirty(value: TankBatteryTagValueState) {
    return !_.isEqual(value.options.graphDisplayOptions, getInitialState().options.graphDisplayOptions) ||
        value.options.durationOptions.option !== getInitialState().options.durationOptions.option ||
        value.options.durationOptions.duration.startDate?.valueOf() !== getInitialState().options.durationOptions.duration.startDate?.valueOf() ||
        value.options.durationOptions.duration.endDate?.valueOf() !== getInitialState().options.durationOptions.duration.endDate?.valueOf();
}

export default function useTankBatteryTagValuesState() {

    const [state, dispatch] = useReducer(tankBatteryTagValuesReducer, getInitialState());

    const resetOptions = useCallback(() => dispatch({
        type: TankBatteryTagValueActionType.ResetOptionsRequested,
    }), []);

    const setDuration = useCallback((value: CustomDurationDialogValues) => dispatch({
        type: TankBatteryTagValueActionType.DurationChanged,
        payload: value
    }), []);

    const setGraphOptions = useCallback((value: ITrendingDisplayOptions) => dispatch({
        type: TankBatteryTagValueActionType.GraphDisplayOptionsChanged,
        payload: value
    }), []);

    return {
        state,
        resetOptions,
        setDuration,
        setGraphOptions
    };
}

